import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import moment from 'moment';
import { GetBeacon4SidePolygons } from '../requests/Beacon4PerformanceRequests';

export const loadBeacon4SidePolygons = createAsyncThunk(
	'beacon4Filters/loadSidePolygons',
	async (_, { rejectWithValue }) => {
		try {
			const res = await GetBeacon4SidePolygons();
			return res?.data ?? res;
		} catch (e) {
			return rejectWithValue(e.response?.data ?? e.message);
		}
	}
);

const beacon4FiltersSlice = createSlice({
	name: 'beacon4Filters',
	initialState: {
		dateFrom:    moment().subtract(1, 'months').format('YYYY-MM-DD'),
		dateTo:      moment().format('YYYY-MM-DD'),
		aggregation: 'Daily',
		side:        '',
		polygon:     '',
		jvid:        '',

		// All pairs from DB — never changes after load
		allPairs: [],   // [{ side, polygon, jvid }]

		// Derived locally
		sides:    [],
		polygons: [],
		jvids:    [],

		visibleSeries: null,        // null = all visible; array = only these shown
		loading:       false,
		error:         null,
	},
	reducers: {
		setBeacon4Aggregation(state, action) { state.aggregation = action.payload; },
		setBeacon4DateFrom(state, action)    { state.dateFrom    = action.payload; },
		setBeacon4DateTo(state, action)      { state.dateTo      = action.payload; },

		setBeacon4Side(state, action) {
			state.side    = action.payload;
			state.polygon = '';
			state.jvid    = '';
			state.jvids   = [];
			state.polygons = [...new Set(
				state.allPairs
					.filter(p => p.side === action.payload)
					.map(p => p.polygon)
			)].sort();
		},

		setBeacon4Polygon(state, action) {
			state.polygon = action.payload;
			state.jvid    = '';
			const selectedPolygons = action.payload
				? action.payload.split(',').map(v => v.trim()).filter(Boolean)
				: [];
			state.jvids = [...new Set(
				state.allPairs
					.filter(p => p.side === state.side &&
						(selectedPolygons.length === 0 || selectedPolygons.includes(p.polygon)))
					.map(p => p.jvid ?? p.Jvid)
					.filter(Boolean)
			)].sort();
		},

		setBeacon4Jvid(state, action) { state.jvid = action.payload; },

		toggleBeacon4Series(state, action) {
			const key = action.payload;
			if (state.visibleSeries === null) {
				// First click — isolate this series only
				state.visibleSeries = [key];
			} else if (state.visibleSeries.includes(key)) {
				// Already visible — remove it
				const next = state.visibleSeries.filter(k => k !== key);
				state.visibleSeries = next.length > 0 ? next : null;
			} else {
				// Add to visible
				state.visibleSeries = [...state.visibleSeries, key];
			}
		},
		resetBeacon4HiddenSeries(state) { state.visibleSeries = null; },
	},
	extraReducers: (b) => {
		b.addCase(loadBeacon4SidePolygons.pending, (s) => { s.loading = true; s.error = null; })
		 .addCase(loadBeacon4SidePolygons.fulfilled, (s, a) => {
				s.loading  = false;
				s.allPairs = a.payload ?? [];
				s.sides    = [...new Set((a.payload ?? []).map(p => p.side))].sort();
				if (s.side) {
					s.polygons = [...new Set(
						(a.payload ?? []).filter(p => p.side === s.side).map(p => p.polygon)
					)].sort();
					if (s.polygon) {
						const sel = s.polygon.split(',').map(v => v.trim()).filter(Boolean);
						s.jvids = [...new Set(
							(a.payload ?? [])
								.filter(p => p.side === s.side && (sel.length === 0 || sel.includes(p.polygon)))
								.map(p => p.jvid ?? p.Jvid)
								.filter(Boolean)
						)].sort();
					}
				}
			})
		 .addCase(loadBeacon4SidePolygons.rejected, (s, a) => { s.loading = false; s.error = a.payload; });
	},
});

export const {
	setBeacon4Aggregation,
	setBeacon4DateFrom, setBeacon4DateTo,
	setBeacon4Side, setBeacon4Polygon, setBeacon4Jvid,
	toggleBeacon4Series, resetBeacon4HiddenSeries,
} = beacon4FiltersSlice.actions;

export default beacon4FiltersSlice.reducer;

export const selectBeacon4Filters       = (state) => state.beacon4Filters;
export const selectBeacon4VisibleSeries = (state) => state.beacon4Filters.visibleSeries;